import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Copy, Upload, ArrowLeft, FileCheck2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Logo, ThemeToggle, Spinner } from "@/components/app/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, tl, dt, num } from "@/lib/api";
import { StatusBadge, METHODS } from "@/lib/labels";

const Row = ({ label, value, copy, testid, strong }) => (
  <div className="flex items-center justify-between gap-4 border-b border-border/60 py-3 last:border-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="flex items-center gap-2 text-right">
      <span className={strong ? "font-display text-lg font-bold" : "font-medium break-all"} data-testid={testid}>{value}</span>
      {copy && <button onClick={() => { navigator.clipboard?.writeText(String(copy)); toast.success("Kopyalandı"); }} className="text-muted-foreground hover:text-primary" data-testid={`${testid}-copy`}><Copy className="h-4 w-4" /></button>}
    </span>
  </div>
);

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/orders/${id}`).then(({ data }) => setO(data)).catch(() => setO(false)), [id]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Dosya en fazla 10 MB olabilir.");
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { await api.post(`/orders/${id}/receipt`, fd); toast.success("Dekont yüklendi. Ödemeniz kısa sürede incelenecek."); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const cancel = async () => {
    try { await api.post(`/orders/${id}/cancel`); toast.success("Sipariş iptal edildi."); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const back = user?.status === "ACTIVE" ? "/panel/satin-al" : "/aktivasyon";
  const b = o?.bank_snapshot || {};
  return (
    <div className="min-h-screen">
      <header className="glass sticky top-0 z-30 flex h-16 items-center justify-between border-x-0 border-t-0 px-4 sm:px-8"><Logo /><ThemeToggle /></header>
      <main className="mx-auto max-w-2xl px-4 py-10 sm:px-8 animate-fade-up">
        <button onClick={() => nav(back)} className="mb-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="order-back"><ArrowLeft className="h-4 w-4" />Geri</button>
        {o === null ? <Spinner /> : o === false ? <p>Sipariş bulunamadı.</p> : (
          <div className="space-y-6" data-testid="order-detail">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div><div className="eyebrow">Sipariş</div><h1 className="mt-1 text-2xl font-bold">{o.product_snapshot.name}</h1></div>
              <StatusBadge status={o.status} testid="order-status" />
            </div>
            {o.payment_method === "IBAN_TRANSFER" && o.status === "PENDING" && (
              <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm" data-testid="iban-instructions">
                Aşağıdaki hesaba <b>{tl(o.final_amount)}</b> tutarında havale/EFT yapın ve açıklamaya <b>{o.order_no}</b> yazın. Ödeme admin onayından sonra kredileriniz yüklenir.
              </div>
            )}
            {o.bank_snapshot && (
              <div className="glass-card p-6" data-testid="bank-details">
                <h2 className="mb-2 font-semibold">Banka Bilgileri</h2>
                <Row label="Banka" value={b.bank_name} testid="bank-name" />
                <Row label="Hesap Sahibi" value={b.account_holder} copy={b.account_holder} testid="bank-holder" />
                <Row label="IBAN" value={b.iban?.replace(/(.{4})/g, "$1 ").trim()} copy={b.iban} testid="bank-iban" />
                {b.description && <p className="mt-3 text-xs text-muted-foreground" data-testid="bank-description">{b.description}</p>}
              </div>
            )}
            <div className="glass-card p-6">
              <h2 className="mb-2 font-semibold">Sipariş Özeti</h2>
              <Row label="Sipariş No" value={o.order_no} copy={o.order_no} testid="order-no" />
              <Row label="Ödeme Yöntemi" value={METHODS[o.payment_method]} testid="order-method" />
              <Row label="Kredi" value={num(o.credits)} testid="order-credits" />
              <Row label="Normal Fiyat" value={tl(o.base_price)} testid="order-base-price" />
              {o.discount_rate > 0 && <Row label={`İndirim (%${num(o.discount_rate)})`} value={`-${tl(o.discount_amount)}`} testid="order-discount" />}
              <Row label="Ödenecek Tutar" value={tl(o.final_amount)} testid="order-final-amount" strong />
              <Row label="Tarih" value={dt(o.created_at)} />
              {o.admin_note && <Row label="Not" value={o.admin_note} />}
            </div>
            {o.payment_method === "IBAN_TRANSFER" && o.status === "PENDING" && (
              <div className="glass-card p-6">
                <h2 className="font-semibold">Dekont Yükle</h2>
                <p className="mt-1 text-sm text-muted-foreground">JPG, PNG, WEBP veya PDF · en fazla 10 MB</p>
                {o.receipt_file_id && <p className="mt-3 flex items-center gap-2 text-sm text-emerald-500" data-testid="receipt-uploaded"><FileCheck2 className="h-4 w-4" />Dekont yüklendi ({dt(o.receipt_uploaded_at)})</p>}
                <label className="mt-4 flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border px-4 py-6 text-sm transition-colors hover:border-primary">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{o.receipt_file_id ? "Yeni dekont yükle" : "Dekont seçin"}
                  <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={(e) => upload(e.target.files?.[0])} data-testid="upload-receipt-input" />
                </label>
                <Button variant="ghost" className="mt-4 w-full text-rose-500" onClick={cancel} data-testid="order-cancel-button">Siparişi İptal Et</Button>
              </div>
            )}
            {o.status === "PAID" && <Button asChild className="btn-glow w-full rounded-xl"><Link to="/panel/olustur">İçerik Oluşturmaya Başla</Link></Button>}
          </div>
        )}
      </main>
    </div>
  );
}
